import { useState } from 'react'
import { request } from './StockAdmin'
import { parseCsv } from './CustomerImport'

/**
 * Jednorazový import adresára (firmy – odberatelia/dodávatelia) z Money S4 alebo CSV. Na rozdiel
 * od importu zákazníkov e-shopu sa tu nič neprideľuje ručne – súbor sa prečíta a rovno importuje,
 * e-mail ani telefón nie sú povinné. Páruje sa podľa IČO, inak podľa názvu firmy.
 */

type PartnerRow = { name: string; ico?: string; dic?: string; icDph?: string; street?: string; city?: string; zip?: string; country?: string; email?: string; phone?: string; contactPerson?: string }
type Summary = { created: number; updated: number; skipped: Array<{ row: number; name: string; reason: string }> }

function directChild(el: Element, tag: string): Element | null {
  for (const c of Array.from(el.children)) if (c.tagName === tag) return c
  return null
}
const text = (el: Element | null) => (el?.textContent ?? '').trim()

/** Firmy z exportu Money S4 (FirmaList > Firma) – meno, IČO/DIČ/IČ DPH, fakturačná adresa, e-mail a telefón (cez SeznamSpojeni). */
export function parseMoneyS4Partners(xmlText: string): PartnerRow[] {
  const doc = new DOMParser().parseFromString(xmlText.replace(/^﻿/, ''), 'application/xml')
  if (doc.querySelector('parsererror')) return []
  const firms = Array.from(doc.getElementsByTagName('Firma')).filter(el => directChild(el, 'Nazev'))
  return firms.map(el => {
    const spojeniList = directChild(el, 'SeznamSpojeni')
    const byId = new Map<string, string>()
    if (spojeniList) for (const sp of Array.from(spojeniList.children)) {
      if (sp.tagName !== 'Spojeni') continue
      const id = sp.getAttribute('ID') ?? ''
      const cislo = text(directChild(sp, 'SpojeniCislo'))
      if (id && cislo) byId.set(id, cislo)
    }
    const emailId = text(directChild(el, 'EmailSpojeni_ID'))
    const phoneId = text(directChild(el, 'TelefonSpojeni1_ID'))
    const adresy = directChild(el, 'Adresy')
    const fakt = adresy ? directChild(adresy, 'FakturacniAdresa') : null
    return {
      name: text(directChild(el, 'Nazev')),
      ico: text(directChild(el, 'ICO')),
      dic: text(directChild(el, 'DIC')),
      icDph: text(directChild(el, 'ICDPH')),
      street: text(fakt && directChild(fakt, 'Ulice')),
      city: text(fakt && directChild(fakt, 'Misto')),
      zip: text(fakt && directChild(fakt, 'KodPsc')),
      country: text(fakt && directChild(fakt, 'NazevStatu')),
      email: emailId ? byId.get(emailId) ?? '' : '',
      phone: phoneId ? byId.get(phoneId) ?? '' : '',
    }
  }).filter(r => r.name)
}

/** CSV export (napr. Excel uložený ako „CSV UTF-8“) – stĺpce sa priradia podľa názvu hlavičky. */
function parseCsvPartners(text: string): PartnerRow[] {
  const [head, ...body] = parseCsv(text)
  if (!head) return []
  const norm = (s: string) => s.toLocaleLowerCase('sk').normalize('NFD').replace(/\p{Diacritic}/gu, '')
  const find = (...needles: string[]) => head.findIndex(h => needles.some(n => norm(h).includes(n)))
  const idx = {
    name: find('nazov', 'firma', 'meno', 'name'), ico: find('ico'), dic: find('dic'), icDph: find('icdph', 'ic dph', 'vat'),
    street: find('ulica', 'adresa', 'street'), city: find('mesto', 'obec', 'city'), zip: find('psc', 'zip'),
    country: find('stat', 'krajina', 'country'), email: find('email', 'mail'), phone: find('telefon', 'mobil', 'phone'),
    contactPerson: find('kontaktna osoba', 'kontakt', 'osoba'),
  }
  const get = (row: string[], i: number) => (i >= 0 ? (row[i] ?? '').trim() : '')
  return body.map(row => ({
    name: get(row, idx.name), ico: get(row, idx.ico), dic: get(row, idx.dic), icDph: get(row, idx.icDph),
    street: get(row, idx.street), city: get(row, idx.city), zip: get(row, idx.zip), country: get(row, idx.country),
    email: get(row, idx.email), phone: get(row, idx.phone), contactPerson: get(row, idx.contactPerson),
  })).filter(r => r.name)
}

const isXml = (file: File, text: string) => file.name.toLowerCase().endsWith('.xml') || file.type.includes('xml') || text.trimStart().startsWith('<')

export default function PartnerImport({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [fileName, setFileName] = useState('')
  const [rows, setRows] = useState<PartnerRow[] | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [summary, setSummary] = useState<Summary | null>(null)

  async function onFile(file: File) {
    setError(''); setSummary(null); setFileName(file.name)
    const text = await file.text()
    const parsed = isXml(file, text) ? parseMoneyS4Partners(text) : parseCsvPartners(text)
    if (!parsed.length) { setRows(null); setError(isXml(file, text) ? 'V XML súbore sa nenašla žiadna firma (očakávaný je export z Money S4).' : 'V súbore sa nenašiel žiadny riadok s názvom firmy.'); return }
    setRows(parsed)
  }

  async function doImport() {
    if (!rows?.length || saving) return
    setSaving(true); setError('')
    try {
      const payload = rows.map(r => Object.fromEntries(Object.entries(r).filter(([, v]) => v)))
      const data = await request('/api/admin/partners/import', { method: 'POST', body: JSON.stringify({ rows: payload }) })
      setSummary(data); onImported()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import sa nepodaril.')
    } finally { setSaving(false) }
  }

  return <div className="modal-backdrop" onClick={onClose}>
    <section className="order-detail ci-panel" role="dialog" aria-modal="true" aria-labelledby="pi-title" onClick={e => e.stopPropagation()}>
      <div className="panel-heading"><h2 id="pi-title">Import adresára</h2><button type="button" className="close-btn" aria-label="Zavrieť" onClick={onClose}>×</button></div>

      {!summary && <div className="ci-upload">
        <p>Podporované súbory: XML export zákazníkov (firiem) z Money S4, alebo CSV zoznam firiem (napr. Excel uložený ako „CSV UTF-8“). E-mail ani telefón nie sú povinné.</p>
        <label className="ci-file-btn">Vybrať súbor…<input type="file" accept=".csv,text/csv,.txt,.xml,text/xml,application/xml" onChange={e => { const f = e.target.files?.[0]; if (f) void onFile(f) }} /></label>
        {fileName && rows && <p className="ci-filename">Súbor <strong>{fileName}</strong> · {rows.length} firiem pripravených na import.</p>}
        {error && <p className="error-message">{error}</p>}
        {rows && <div className="ci-actions"><button type="button" className="primary-btn" disabled={saving} onClick={() => void doImport()}>{saving ? 'Importujem…' : `Importovať ${rows.length} firiem`}</button></div>}
      </div>}

      {summary && <div className="ci-upload">
        <p className="success-message">Hotovo – založených {summary.created}, doplnených {summary.updated}{summary.skipped.length ? `, vynechaných ${summary.skipped.length}` : ''}.</p>
        {summary.skipped.length > 0 && <ul className="ci-skip-list">{summary.skipped.slice(0, 20).map((s, i) => <li key={i}>Riadok {s.row} ({s.name || 'bez názvu'}): {s.reason}</li>)}</ul>}
        <div className="ci-actions"><button type="button" className="primary-btn" onClick={onClose}>Zavrieť</button></div>
      </div>}
    </section>
  </div>
}
